import type {
  DocumentTemplateParameter,
  ParameterValue,
  ParameterValues,
} from "@/types/DocumentTemplateParameter";

export function normalizeParameterValue(
  definition: DocumentTemplateParameter,
  value: ParameterValue
): ParameterValue {
  if (value === null || value === "") return null;
  if (
    (definition.type === "integer" || definition.type === "decimal") &&
    typeof value === "string"
  ) {
    if (!value.trim()) return null;
    return Number(value.replace(",", "."));
  }
  return value;
}

export function parameterValueError(
  definition: DocumentTemplateParameter,
  value: ParameterValue
): string | undefined {
  if (value === null || value === "")
    return definition.required ? "field_required" : undefined;
  let valid = true;
  switch (definition.type) {
    case "integer":
    case "decimal":
      valid =
        typeof value === "number" &&
        Number.isFinite(value) &&
        (definition.type !== "integer" || Number.isSafeInteger(value)) &&
        (definition.min === undefined || value >= definition.min) &&
        (definition.max === undefined || value <= definition.max);
      break;
    case "text":
      if (
        typeof value === "string" &&
        value.length > (definition.max_length ?? 10000)
      )
        return "document_parameters.text_too_long";
      valid = typeof value === "string";
      break;
    case "date": {
      const date =
        typeof value === "string"
          ? new Date(`${value}T00:00:00Z`)
          : new Date(NaN);
      valid =
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(value) &&
        Number.isFinite(date.getTime()) &&
        date.toISOString().slice(0, 10) === value;
      break;
    }
    case "boolean":
      valid = typeof value === "boolean";
      break;
    case "select":
      valid = !!definition.choices?.some((choice) => choice.value === value);
  }
  return valid ? undefined : "document_parameters.invalid_value";
}

export function validateParameterValues(
  definitions: DocumentTemplateParameter[],
  values: ParameterValues
) {
  const normalized: ParameterValues = {};
  const errors: Record<string, string> = {};
  for (const definition of definitions) {
    const value = normalizeParameterValue(
      definition,
      values[definition.name] ?? null
    );
    normalized[definition.name] = value;
    const error = parameterValueError(definition, value);
    if (error) errors[definition.name] = error;
  }
  return { values: normalized, errors };
}
